/**
 * Sprint Redesign v1 (Do 03.09.2026) — Redesign-Sidebar.
 * I27 Update (04.09.2026, 01:00): Mobile-Drawer via mobileOpen/onMobileClose Prop.
 *
 * Auf Desktop (lg+): Persistente Sidebar mit Ein-/Ausklappen (localStorage).
 * Auf Mobile (<lg): Off-Canvas-Drawer mit Backdrop, ESC + Klick-außerhalb schließt.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, User, Eye, DoorOpen, PlayCircle, UserCircle,
  ChevronLeft, ChevronRight, X,
} from "lucide-react";
import brandMark from "@/assets/brand/f4s-logo.png";

const STORAGE_KEY = "f4s_sidebar_collapsed";

const MAIN_ITEMS = [
  { to: "/dashboard", label: "Mein PocketCoach", sub: null, icon: LayoutDashboard, testId: "sidebar-dashboard" },
  { to: "/mein-muster", label: "So kommuniziere ich", sub: "Wahrnehmung und Reaktion", icon: User, testId: "sidebar-mein-muster" },
  { to: "/andere-verstehen", label: "Mein Gegenüber", sub: "Andere erkennen und verstehen", icon: Eye, testId: "sidebar-andere-verstehen" },
  { to: "/wirkung-gestalten", label: "Kommunikation als Türöffner", sub: "Wirkung bewusst gestalten", icon: DoorOpen, testId: "sidebar-wirkung-gestalten" },
  { to: "/videokurs", label: "Hintergrundwissen", sub: "Videokurse und mehr", icon: PlayCircle, testId: "sidebar-videokurs" },
];

const SECONDARY_ITEMS = [
  { to: "/profil", label: "Mein Profil", sub: null, icon: UserCircle, testId: "sidebar-profil" },
];

// Issue #7 (Pre-Launch Do10.09.2026, 15:30) — 404-Risiko entschärft.
// Legal-Links-Update (Do10.09.2026, 18:00) — Umstellung von Kanzlei-Domain
// (aj-kommunikation.de) auf die konsistente Marken-Domain effektiv-kommunizieren.de
// (Flex4Success ist Subdomain davon). URLs vorab per curl auf HTTP 200 verifiziert.
const FOOTER_LINKS = [
  {
    href: "https://effektiv-kommunizieren.de/datenschutz",
    label: "Datenschutz",
    external: true,
  },
  {
    href: "https://effektiv-kommunizieren.de/impressum",
    label: "Impressum",
    external: true,
  },
];

export default function RedesignSidebar({ mobileOpen = false, onMobileClose = () => {} }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "true"; } catch { return false; }
  });
  // M4 (Postlaunch-V3, Fr 04.09.2026) — Sub-Labels nur on-hover sichtbar,
  // ausser das Nav-Item ist aktuell aktiv (bleibt persistent zur Orientierung).
  const [hoveredKey, setHoveredKey] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "true" : "false"); } catch { /* ignore */ }
  }, [collapsed]);

  const toggleCollapsed = () => setCollapsed((c) => !c);

  const isActive = (to) => {
    if (to === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  const renderItem = (item, isSecondary, { isMobile = false } = {}) => {
    const Icon = item.icon;
    const active = isActive(item.to);
    // Sub-Label sichtbar wenn: mobile-drawer offen, active, oder gerade gehovered
    const showSub = item.sub && (isMobile || active || hoveredKey === item.to);
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onMobileClose}
        onMouseEnter={() => setHoveredKey(item.to)}
        onMouseLeave={() => setHoveredKey((k) => (k === item.to ? null : k))}
        onFocus={() => setHoveredKey(item.to)}
        onBlur={() => setHoveredKey((k) => (k === item.to ? null : k))}
        data-testid={item.testId}
        data-active={active ? "true" : "false"}
        data-sub-visible={showSub ? "true" : "false"}
        aria-current={active ? "page" : undefined}
        className={[
          "group flex items-center gap-3 rounded-xl transition-all",
          collapsed ? "px-2 py-2.5 justify-center" : "px-3 py-2.5",
          active
            ? "bg-[var(--fts-primary-bright)] text-white shadow-sm"
            : isSecondary
              ? "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800",
        ].join(" ")}
      >
        <Icon className={["flex-shrink-0", collapsed ? "w-5 h-5" : "w-5 h-5"].join(" ")} aria-hidden="true" />
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className={["text-base font-bold leading-tight", isSecondary && !active ? "font-semibold" : ""].join(" ")}>
              {item.label}
            </p>
            {item.sub && (
              <p
                data-testid={`${item.testId}-sub`}
                className={[
                  "text-[13px] leading-snug overflow-hidden transition-all duration-150 ease-out",
                  showSub ? "opacity-100 max-h-8 mt-0.5" : "opacity-0 max-h-0 mt-0",
                  active ? "text-white/85" : "text-slate-500 dark:text-slate-400",
                ].join(" ")}
              >
                {item.sub}
              </p>
            )}
          </div>
        )}
      </Link>
    );
  };

  const asideBase =
    "flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex-shrink-0";
  const asideDesktop = [
    "hidden lg:flex sticky top-0 h-screen transition-all duration-200",
    collapsed ? "w-16" : "w-72",
  ].join(" ");
  const asideMobile = [
    "lg:hidden fixed inset-y-0 left-0 z-50 w-72 shadow-2xl transition-transform duration-300",
    mobileOpen ? "translate-x-0" : "-translate-x-full",
  ].join(" ");

  const renderNav = (isMobile) => (
    <>
      <div className={["flex items-center border-b border-slate-100 dark:border-slate-800", (collapsed && !isMobile) ? "px-2 py-4 justify-center" : "px-4 py-4"].join(" ")}>
        <Link to="/dashboard" onClick={onMobileClose} data-testid="sidebar-logo-link" className="flex items-center gap-2 min-w-0 flex-1">
          <img
            src={brandMark}
            alt="Flex4Success"
            data-testid={isMobile ? "sidebar-brand-mark-mobile" : "sidebar-brand-mark"}
            className={["w-auto flex-shrink-0", (collapsed && !isMobile) ? "h-8" : "h-10"].join(" ")}
          />
          {(!collapsed || isMobile) && (
            <span className="font-extrabold text-slate-900 dark:text-white text-sm truncate">
              Flex4Success
            </span>
          )}
        </Link>
        {isMobile && (
          <button
            type="button"
            onClick={onMobileClose}
            data-testid="sidebar-mobile-close"
            aria-label="Menü schließen"
            className="ml-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 p-2"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {!isMobile && (
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="sidebar-toggle"
          aria-label={collapsed ? "Sidebar ausklappen" : "Sidebar einklappen"}
          aria-expanded={!collapsed}
          className="mx-3 my-2 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-300 py-1.5 transition-colors"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      )}

      <nav className={["flex-1 overflow-y-auto space-y-1", (collapsed && !isMobile) ? "px-2 pt-2" : "px-3 pt-2"].join(" ")} aria-label="Hauptbereich">
        {MAIN_ITEMS.map((i) => renderItem(i, false, { isMobile }))}
        <div className={["my-3 border-t border-slate-100 dark:border-slate-800", (collapsed && !isMobile) ? "mx-1" : ""].join(" ")} />
        {SECONDARY_ITEMS.map((i) => renderItem(i, true, { isMobile }))}
      </nav>

      {(!collapsed || isMobile) && (
        <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-3 space-y-1.5" data-testid={isMobile ? "sidebar-footer-mobile" : "sidebar-footer"}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
            Rechtliches
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {FOOTER_LINKS.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
                data-testid={`sidebar-footer-${l.label.toLowerCase()}`}
                className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:underline">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Desktop-Sidebar */}
      <aside
        className={[asideBase, asideDesktop].join(" ")}
        data-testid="redesign-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        aria-label="Hauptnavigation"
      >
        {renderNav(false)}
      </aside>

      {/* Mobile-Drawer */}
      <aside
        className={[asideBase, asideMobile].join(" ")}
        data-testid="redesign-sidebar-mobile"
        data-open={mobileOpen ? "true" : "false"}
        aria-label="Mobile Hauptnavigation"
        aria-hidden={!mobileOpen}
      >
        {renderNav(true)}
      </aside>
    </>
  );
}
